import { useMutation, useQueryClient } from '@tanstack/react-query';

import axios, { parseAxiosError } from '@/portainer/services/axios';
import { withError } from '@/react-tools/react-query';

import { EnvironmentGroupId } from '../../types';
import { EnvironmentGroup } from '../types';
import { buildUrl } from '../queries/build-url';
import { queryKeys } from '../queries/query-keys';

interface UpdateGroupAccessPayload {
  id: EnvironmentGroupId;
  userAccessPolicies: Record<number, { RoleId: number }>;
  teamAccessPolicies: Record<number, { RoleId: number }>;
}

async function updateGroupAccess({
  id,
  userAccessPolicies,
  teamAccessPolicies,
}: UpdateGroupAccessPayload) {
  try {
    const { data } = await axios.put<EnvironmentGroup>(buildUrl(id), {
      UserAccessPolicies: userAccessPolicies,
      TeamAccessPolicies: teamAccessPolicies,
    });
    return data;
  } catch (e) {
    throw parseAxiosError(e as Error, 'Unable to update group access');
  }
}

export function useUpdateGroupAccessMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateGroupAccess,
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries(queryKeys.list(false)),
        queryClient.invalidateQueries(queryKeys.list(true)),
        queryClient.invalidateQueries(queryKeys.group(variables.id)),
        queryClient.invalidateQueries(queryKeys.group(variables.id, true)),
      ]);
    },
    ...withError('Unable to update group access'),
  });
}
